// Paginated failures list. Status badges carry
// text + color + an icon affordance (never color-only).

import { AlertOctagon, CheckCircle2, Eye } from "lucide-react";
import { Badge } from "@/design-system/components/ui";
import type {
  DealFailureDto,
  FailureStatus,
  PagedFailures,
} from "@/api/dealFlow/types";

interface Props {
  page: PagedFailures;
  sort: string;
  onSortChange: (sort: string) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onOpen: (id: string) => void;
}

const STATUS_VARIANT: Record<
  FailureStatus,
  { variant: "error" | "review" | "finalized"; icon: typeof AlertOctagon; label: string }
> = {
  Open: { variant: "error", icon: AlertOctagon, label: "Open" },
  Reviewed: { variant: "review", icon: Eye, label: "Reviewed" },
  Resolved: { variant: "finalized", icon: CheckCircle2, label: "Resolved" },
};

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  const ms = Date.now() - then;
  const min = Math.round(ms / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.round(hr / 24);
  return `${d}d ago`;
}

export function FailureList({
  page,
  sort,
  onSortChange,
  onPageChange,
  onPageSizeChange,
  onOpen,
}: Props) {
  const { items, page: pageNo, pageSize, totalCount } = page;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <div className="flex flex-col gap-space-3">
      <div className="flex items-center justify-between text-small text-gray-mid">
        <div role="status" aria-live="polite">
          {totalCount} failure{totalCount === 1 ? "" : "s"}
        </div>
        <div className="flex items-center gap-space-2">
          <label htmlFor="failure-sort" className="sr-only">
            Sort failures
          </label>
          <select
            id="failure-sort"
            className="border border-border-light rounded px-space-2 py-[2px]"
            value={sort}
            onChange={(e) => onSortChange(e.target.value)}
          >
            <option value="createdAt:desc">Newest first</option>
            <option value="createdAt:asc">Oldest first</option>
            <option value="lane:asc">Lane A→Z</option>
            <option value="lane:desc">Lane Z→A</option>
            <option value="status:asc">Status A→Z</option>
          </select>
        </div>
      </div>

      <table className="w-full text-body" aria-label="Failures">
        <thead>
          <tr className="text-left text-label uppercase tracking-label text-gray-mid">
            <th scope="col" className="py-space-2">Status</th>
            <th scope="col" className="py-space-2">Lane</th>
            <th scope="col" className="py-space-2">Reason</th>
            <th scope="col" className="py-space-2">Analyst</th>
            <th scope="col" className="py-space-2">Created</th>
            <th scope="col" className="py-space-2 sr-only">Action</th>
          </tr>
        </thead>
        <tbody>
          {items.map((f) => (
            <FailureRow key={f.id} failure={f} onOpen={onOpen} />
          ))}
        </tbody>
      </table>

      <div className="flex items-center justify-between mt-space-2">
        <div className="flex items-center gap-space-2 text-small text-gray-mid">
          <label htmlFor="failure-page-size" className="sr-only">
            Page size
          </label>
          <select
            id="failure-page-size"
            className="border border-border-light rounded px-space-2 py-[2px]"
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
          >
            <option value={25}>25 / page</option>
            <option value={50}>50 / page</option>
            <option value={100}>100 / page</option>
          </select>
        </div>

        <div className="flex items-center gap-space-2">
          <button
            type="button"
            className="px-space-2 py-[2px] border border-border-light rounded disabled:opacity-50"
            onClick={() => onPageChange(Math.max(1, pageNo - 1))}
            disabled={pageNo <= 1}
            aria-label="Previous page"
          >
            ‹ Prev
          </button>
          <span className="text-small text-gray-mid" aria-live="polite">
            Page {pageNo} of {totalPages}
          </span>
          <button
            type="button"
            className="px-space-2 py-[2px] border border-border-light rounded disabled:opacity-50"
            onClick={() => onPageChange(Math.min(totalPages, pageNo + 1))}
            disabled={pageNo >= totalPages}
            aria-label="Next page"
          >
            Next ›
          </button>
        </div>
      </div>
    </div>
  );
}

function FailureRow({
  failure,
  onOpen,
}: {
  failure: DealFailureDto;
  onOpen: (id: string) => void;
}) {
  const visual = STATUS_VARIANT[failure.status];
  const Icon = visual.icon;
  return (
    <tr
      className="border-t border-border-light hover:bg-white-warm cursor-pointer"
      onClick={() => onOpen(failure.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(failure.id);
        }
      }}
      tabIndex={0}
      role="button"
      aria-label={`Open failure ${failure.id}`}
    >
      <td className="py-space-2">
        <Badge status={visual.variant} aria-label={`Status: ${visual.label}`}>
          <Icon size={12} aria-hidden />
          <span className="ml-space-1">{visual.label}</span>
        </Badge>
      </td>
      <td className="py-space-2 font-mono">{failure.lane}</td>
      <td className="py-space-2 max-w-md truncate" title={failure.failureReason}>
        {failure.failureReason}
      </td>
      <td className="py-space-2 text-gray-mid">{failure.analystUserId}</td>
      <td className="py-space-2 text-gray-mid">{timeAgo(failure.createdAt)}</td>
      <td className="py-space-2 text-gold underline">View →</td>
    </tr>
  );
}
